import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Socket } from 'socket.io-client';
import { CloseButton } from './CloseButton';
import { CourierBoardInline } from './CourierBoardInline';
import { useDraggablePanel } from '../utils/panelDragUtils';
import { MarketplaceExchangePanel } from './MarketplaceExchangePanel';

interface ItemDefinition {
  id: string;
  name: string;
  description: string;
  type: 'weapon' | 'armor' | 'accessory' | 'consumable' | 'material';
  rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
  equipSlot?: string;
  stats?: Record<string, number>;
  buyPrice: number;
  sellPrice: number;
  icon: string;
}

interface ShopItem extends ItemDefinition {
  itemId: string;
  stock: number;
  maxStock: number;
  currentBuyPrice: number;
  currentSellPrice: number;
}

interface InventoryItem {
  itemId: string;
  quantity: number;
  definition: ItemDefinition | null;
}

interface TradingPostPrice {
  locationName: string;
  locationId: string;
  buyPrice: number;
  sellPrice: number;
  x: number;
  y: number;
}

interface MarketplacePanelProps {
  socket: Socket | null;
  isOpen: boolean;
  onClose: () => void;
  pieces: number;
  onPiecesChange?: (amount: number) => void;
  tradingPostName: string;
  onSetDestination?: (x: number, y: number, name: string) => void;
  locationId: string;
  locationType: 'trading_post' | 'harbor_office';
  hasCourier?: boolean;
}

const RARITY_COLORS: Record<string, string> = {
  common: '#9e9e9e',
  uncommon: '#4caf50',
  rare: '#2196f3',
  epic: '#9c27b0',
  legendary: '#ff9800',
};

const CATEGORIES = [
  { id: 'all', label: 'All Items' },
  { id: 'weapon', label: 'Weapons' },
  { id: 'armor', label: 'Armor' },
  { id: 'accessory', label: 'Accessories' },
  { id: 'consumable', label: 'Consumables' },
  { id: 'material', label: 'Materials' },
];

const renderItemIcon = (icon: string | undefined, name: string): JSX.Element => {
  const publicUrl: string = process.env.PUBLIC_URL || '';
  const resolvedSrc = icon && icon.startsWith('/assets')
    ? `${publicUrl}${icon}`
    : icon && (icon.startsWith('http://') || icon.startsWith('https://'))
    ? icon
    : `${publicUrl}/assets/unknown.png`;
  
  const fallbackChain = [
    `${publicUrl}/assets/unknown.png`,
  ];
  let currentFallbackIndex = 0;
  
  return (
    <img
      src={resolvedSrc}
      alt={name}
      style={{ width: 48, height: 48, objectFit: 'contain' }}
      onError={(e) => {
        const target = e.target as HTMLImageElement;
        currentFallbackIndex++;
        
        if (currentFallbackIndex < fallbackChain.length) {
          const nextFallback = fallbackChain[currentFallbackIndex];
          if (target.src !== nextFallback) {
            target.src = nextFallback;
            return;
          }
        }
        
        target.onerror = null;
      }}
    />
  );
};

export const MarketplacePanel: React.FC<MarketplacePanelProps> = ({
  socket,
  isOpen,
  onClose,
  pieces,
  onPiecesChange,
  tradingPostName,
  onSetDestination,
  locationId,
  locationType,
  hasCourier = false,
}) => {
  const [shopItems, setShopItems] = useState<ShopItem[]>([]);
  const [playerItems, setPlayerItems] = useState<InventoryItem[]>([]);
  const [activeTab, setActiveTab] = useState<'buy' | 'sell' | 'exchange' | 'courier'>('buy');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedItem, setSelectedItem] = useState<ShopItem | null>(null);
  const [selectedSellItem, setSelectedSellItem] = useState<InventoryItem | null>(null);
  const [message, setMessage] = useState<string>('');
  const [messageType, setMessageType] = useState<'success' | 'error'>('success');
  const [nextRestock, setNextRestock] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [alternatePrices, setAlternatePrices] = useState<TradingPostPrice[]>([]);
  const [quantity, setQuantity] = useState<number>(1);
  const [restockTick, setRestockTick] = useState<number>(0);
  const { handleHeaderMouseDown, style: dragStyle, panelRef } = useDraggablePanel(100, 100, {
    storageKey: 'panel:marketplace',
    clampToViewport: true,
  });

  useEffect(() => {
    if (isOpen && socket) {
      setIsLoading(true);
      setSelectedItem(null);
      setSelectedSellItem(null);
      setQuantity(1);
      socket.emit('inventory:shop');
      socket.emit('inventory:get');
    }
  }, [isOpen, socket]);

  useEffect(() => {
    if (!socket) return;

    const handleShopData = (data: { items: ShopItem[]; nextRestock: number }): void => {
      setShopItems(data.items);
      setNextRestock(data.nextRestock);
      setIsLoading(false);
    };

    const handleInventoryUpdate = (data: { items: InventoryItem[] }): void => {
      setPlayerItems(data.items);
    };

    const handleBuyResult = (data: { success: boolean; message: string; newBalance?: number }): void => {
      setMessage(data.message);
      setMessageType(data.success ? 'success' : 'error');
      if (data.success) {
        socket.emit('inventory:shop');
        socket.emit('inventory:get');
        if (typeof data.newBalance === 'number' && onPiecesChange) {
          onPiecesChange(data.newBalance);
        }
      }
      setTimeout(() => setMessage(''), 3000);
    };

    const handleSellResult = (data: { success: boolean; message: string; newBalance?: number }): void => {
      setMessage(data.message);
      setMessageType(data.success ? 'success' : 'error');
      if (data.success) {
        socket.emit('inventory:shop');
        socket.emit('inventory:get');
        if (typeof data.newBalance === 'number' && onPiecesChange) {
          onPiecesChange(data.newBalance);
        }
        setSelectedSellItem(null);
      }
      setTimeout(() => setMessage(''), 3000);
    };

    const handleRestocked = (data?: { nextRestock?: number }): void => {
      if (typeof data?.nextRestock === 'number') {
        setNextRestock(data.nextRestock);
      }
      socket.emit('inventory:shop');
    };

    const handleAlternatePrices = (data: { itemId: string; prices: TradingPostPrice[] }): void => {
      setAlternatePrices(data.prices);
    };

    const handleInventoryError = (data: { message: string }): void => {
      if (typeof data?.message !== 'string') return;
      setMessage(data.message);
      setMessageType('error');
      setTimeout(() => setMessage(''), 3000);
    };

    socket.on('inventory:shop', handleShopData);
     socket.on('inventory:data', handleInventoryUpdate);
     socket.on('inventory:buy:result', handleBuyResult);
     socket.on('inventory:sell:success', handleSellResult);
     socket.on('inventory:error', handleInventoryError);
     socket.on('shop:restocked', handleRestocked);
     socket.on('shop:alternate-prices', handleAlternatePrices);

     return () => {
       socket.off('inventory:shop', handleShopData);
       socket.off('inventory:data', handleInventoryUpdate);
       socket.off('inventory:buy:result', handleBuyResult);
       socket.off('inventory:sell:success', handleSellResult);
       socket.off('inventory:error', handleInventoryError);
       socket.off('shop:restocked', handleRestocked);
       socket.off('shop:alternate-prices', handleAlternatePrices);
    };
  }, [socket, onPiecesChange]);

  useEffect(() => {
    if (!isOpen) return;

    const interval = window.setInterval(() => {
      setRestockTick((v) => v + 1);
    }, 1000);

    return () => window.clearInterval(interval);
  }, [isOpen]);

  const filteredItems = useMemo(() => {
    let items = shopItems;
    if (selectedCategory !== 'all') {
      items = items.filter((item) => item.type === selectedCategory);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      items = items.filter(
        (item) =>
          item.name.toLowerCase().includes(q) ||
          item.description.toLowerCase().includes(q)
      );
    }
    return items;
  }, [shopItems, selectedCategory, searchQuery]);

  const filteredSellItems = useMemo(() => {
    let items = playerItems.filter((i) => i.definition);
    if (selectedCategory !== 'all') {
      items = items.filter((item) => item.definition?.type === selectedCategory);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      items = items.filter(
        (item) =>
          item.definition?.name.toLowerCase().includes(q) ||
          item.definition?.description.toLowerCase().includes(q)
      );
    }
    return items;
  }, [playerItems, selectedCategory, searchQuery]);

  const formatRestockTime = (): string => {
    const now = Date.now() + restockTick * 0;
    const remaining = Math.max(0, nextRestock - now);
    const minutes = Math.floor(remaining / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleBuy = useCallback((): void => {
    if (!selectedItem) return;
    socket?.emit('inventory:buy', { itemId: selectedItem.itemId, quantity });
  }, [selectedItem, quantity, socket]);

  const handleSell = useCallback((): void => {
    if (!selectedSellItem) return;
    socket?.emit('inventory:sell', { itemId: selectedSellItem.itemId, quantity });
  }, [selectedSellItem, quantity, socket]);

  const handleSellAll = useCallback((): void => {
    if (!socket) return;
    // Sell all inventory items currently sellable (respecting filteredSellItems)
    filteredSellItems.forEach((item) => {
      if (item.quantity > 0) {
        socket.emit('inventory:sell', { itemId: item.itemId, quantity: item.quantity });
      }
    });
    // Clear selection and reset quantity
    setSelectedSellItem(null);
    setQuantity(1);
    setMessage('Selling all sellable items...');
    setMessageType('success');
    setTimeout(() => setMessage(''), 2000);
  }, [socket, filteredSellItems]);

  const handleSelectItem = useCallback((item: ShopItem): void => {
    setSelectedItem(item);
    setSelectedSellItem(null);
    setQuantity(1);
    setAlternatePrices([]);
    socket?.emit('shop:get-alternate-prices', { itemId: item.itemId });
  }, [socket]);

  const handleSelectSellItem = useCallback((item: InventoryItem): void => {
    setSelectedSellItem(item);
    setSelectedItem(null);
    setQuantity(1);
    setAlternatePrices([]);
    socket?.emit('shop:get-alternate-prices', { itemId: item.itemId });
  }, [socket]);

  useEffect(() => {
    if (activeTab === 'buy' && filteredItems.length > 0 && !selectedItem) {
      handleSelectItem(filteredItems[0]);
    }
  }, [activeTab, filteredItems, selectedItem, handleSelectItem]);

  useEffect(() => {
    if (activeTab === 'sell' && filteredSellItems.length > 0 && !selectedSellItem) {
      handleSelectSellItem(filteredSellItems[0]);
    }
  }, [activeTab, filteredSellItems, selectedSellItem, handleSelectSellItem]);

  if (!isOpen) return null;

  const currentItem = activeTab === 'buy' ? selectedItem : selectedSellItem?.definition;
  const maxQuantity =
    activeTab === 'buy'
      ? selectedItem?.stock || 1
      : selectedSellItem?.quantity || 1;

  return (
    <div
      ref={panelRef}
      style={{
        position: 'fixed',
        ...dragStyle,
        zIndex: 5000,
      }}
    >
      <div style={{ ...styles.panelContent, background: 'linear-gradient(180deg, #0d1117 0%, #0a0e14 100%)', border: '2px solid #21262d' }}>
        {/* Header */}
        <div style={styles.header} onMouseDown={handleHeaderMouseDown}>
          <div style={styles.headerLeft}>
            <span style={styles.headerIcon}></span>
            <div>
              <h2 style={{...styles.title}} className="font-lookout-24">{tradingPostName}</h2>
              <span style={{...styles.subtitle, fontFamily: 'var(--font-mono)', fontSize: '12px'}}>Regional Marketplace</span>
            </div>
          </div>
          <div style={styles.headerRight}>
            <div style={styles.balanceBox}>
              <span style={styles.balanceLabel}>BALANCE</span>
              <span style={styles.balanceValue}>{pieces.toLocaleString()}₱</span>
            </div>
            <div style={styles.restockBox}>
              <span style={styles.restockLabel}>RESTOCK</span>
              <span style={styles.restockValue}>{formatRestockTime()}</span>
            </div>
            <CloseButton onClick={onClose} />
          </div>
        </div>

        {/* Message */}
        {message && (
          <div
            style={{
              ...styles.message,
              background: messageType === 'success' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
              borderColor: messageType === 'success' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)',
              color: messageType === 'success' ? '#22c55e' : '#ef4444',
            }}
          >
            {message}
          </div>
        )}

        {/* Inline Tab Row */}
        <div style={styles.tabRow}>
          <div style={{ display: 'flex', gap: 8, flex: 1 }}>
            <button
              style={{
                ...styles.tab,
                ...(activeTab === 'buy' ? styles.tabActive : {}),
                flex: 1,
              }}
              onClick={() => {
                setActiveTab('buy');
                setSelectedItem(null);
                setSelectedSellItem(null);
              }}
            >
              BUY
            </button>
            <button
              style={{
                ...styles.tab,
                ...(activeTab === 'sell' ? styles.tabActive : {}),
                flex: 1,
              }}
              onClick={() => {
                setActiveTab('sell');
                setSelectedItem(null);
                setSelectedSellItem(null);
              }}
            >
              SELL
            </button>
            <button
              style={{
                ...styles.tab,
                ...(activeTab === 'exchange' ? styles.tabActive : {}),
                flex: 1,
              }}
              onClick={() => {
                setActiveTab('exchange');
                setSelectedItem(null);
                setSelectedSellItem(null);
              }}
            >
              EXCHANGE
            </button>
            {hasCourier && (
              <button
                style={{
                  ...styles.tab,
                  ...(activeTab === 'courier' ? styles.tabActive : {}),
                  flex: 1,
                }}
                onClick={() => {
                  setActiveTab('courier');
                  setSelectedItem(null);
                  setSelectedSellItem(null);
                }}
              >
                COURIER
              </button>
            )}
          </div>
          {activeTab === 'sell' && (
            <button
              style={{
                ...styles.tab,
                backgroundColor: '#0b5c2c',
                color: '#e0f2f1',
                fontWeight: 700,
                padding: '8px 12px',
                marginLeft: 8,
              }}
              onClick={handleSellAll}
            >
              SELL ALL
            </button>
          )}
        </div>

        {/* Main Content */}
        <div style={styles.content}>
          {activeTab === 'courier' ? (
            <CourierBoardInline
              socket={socket}
              locationId={locationId}
              locationName={tradingPostName}
              locationType={locationType}
              onSetDestination={onSetDestination}
            />
          ) : activeTab === 'exchange' ? (
            <MarketplaceExchangePanel
              socket={socket}
              inventory={playerItems}
              pieces={pieces}
              onPiecesChange={onPiecesChange}
            />
          ) : (
          <>
          {/* Left Sidebar - Categories */}
          <div style={styles.sidebar}>
            <div style={styles.searchBox}>
              <input
                type="text"
                placeholder="Search items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={styles.searchInput}
              />
            </div>
            <div style={styles.categoryList} className="marketplace-scrollbar">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  style={{
                    ...styles.categoryBtn,
                    ...(selectedCategory === cat.id ? styles.categoryBtnActive : {}),
                  }}
                  onClick={() => setSelectedCategory(cat.id)}
                >
                  <span>{cat.label}</span>
                  <span style={styles.categoryCount}>
                    {cat.id === 'all'
                      ? activeTab === 'buy'
                        ? shopItems.length
                        : playerItems.filter((i) => i.definition).length
                      : activeTab === 'buy'
                      ? shopItems.filter((i) => i.type === cat.id).length
                      : playerItems.filter((i) => i.definition?.type === cat.id).length}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Center - Item List View */}
          <div style={{...styles.itemGrid, flexDirection: 'column', padding: '0', overflowY: 'auto'}} className="marketplace-scrollbar">
            {isLoading ? (
              <div style={styles.loadingGrid}>
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} style={{...styles.skeletonCard, height: '48px', marginBottom: '8px'}} />
                ))}
              </div>
            ) : activeTab === 'buy' ? (
              filteredItems.length === 0 ? (
                <div style={styles.emptyState}>No items found</div>
              ) : (
                <div style={{display: 'flex', flexDirection: 'column', gap: '1px', overflowY: 'auto', flex: 1}}>
                  {filteredItems.map((item) => (
                    <div
                      key={item.itemId}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '10px 12px',
                        backgroundColor: selectedItem?.itemId === item.itemId ? 'rgba(33, 150, 243, 0.15)' : '#0f172a',
                        borderLeft: `3px solid ${RARITY_COLORS[item.rarity]}`,
                        cursor: 'pointer',
                        opacity: item.stock === 0 ? 0.5 : 1,
                        transition: 'background-color 0.2s',
                      }}
                      onClick={() => handleSelectItem(item)}
                      onMouseEnter={(e) => {
                        if (selectedItem?.itemId !== item.itemId) {
                          (e.currentTarget as HTMLElement).style.backgroundColor = '#1a2332';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedItem?.itemId !== item.itemId) {
                          (e.currentTarget as HTMLElement).style.backgroundColor = '#0f172a';
                        }
                      }}
                    >
                      <div style={{width: '40px', height: '40px', marginRight: '12px', flexShrink: 0}}>
                        {renderItemIcon(item.icon, item.name)}
                      </div>
                      <div style={{flex: 1}}>
                        <div style={{color: RARITY_COLORS[item.rarity], fontWeight: 600, fontSize: '13px'}}>
                          {item.name}
                        </div>
                        <div style={{fontSize: '9px', color: '#fff', marginTop: '4px', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 'bold', fontFamily: '"Sometype Mono", monospace', display: 'inline-block', background: '#1f6feb', padding: '2px 6px', borderRadius: 0}}>
                          {item.type}
                        </div>
                      </div>
                      <div style={{textAlign: 'right', marginLeft: '12px', flexShrink: 0}}>
                        <div style={{color: '#fbbf24', fontWeight: 600, fontSize: '13px'}}>
                          {item.currentBuyPrice.toLocaleString()}₱
                        </div>
                        <div style={{color: item.stock === 0 ? '#ef4444' : '#8b949e', fontSize: '11px', marginTop: '2px'}}>
                          Stock: {item.stock}/{item.maxStock}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : filteredSellItems.length === 0 ? (
              <div style={styles.emptyState}>No items to sell</div>
            ) : (
              <div style={{display: 'flex', flexDirection: 'column', gap: '1px', overflowY: 'auto', flex: 1}}>
                {filteredSellItems.map((item) => {
                  const def = item.definition!;
                  const shopItem = shopItems.find((s) => s.itemId === item.itemId);
                  const sellPrice = shopItem?.currentSellPrice || def.sellPrice;
                  return (
                    <div
                      key={item.itemId}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '10px 12px',
                        backgroundColor: selectedSellItem?.itemId === item.itemId ? 'rgba(34, 197, 94, 0.15)' : '#0f172a',
                        borderLeft: `3px solid ${RARITY_COLORS[def.rarity]}`,
                        cursor: 'pointer',
                        transition: 'background-color 0.2s',
                      }}
                      onClick={() => handleSelectSellItem(item)}
                      onMouseEnter={(e) => {
                        if (selectedSellItem?.itemId !== item.itemId) {
                          (e.currentTarget as HTMLElement).style.backgroundColor = '#1a2332';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedSellItem?.itemId !== item.itemId) {
                          (e.currentTarget as HTMLElement).style.backgroundColor = '#0f172a';
                        }
                      }}
                    >
                      <div style={{width: '40px', height: '40px', marginRight: '12px', flexShrink: 0}}>
                        {renderItemIcon(def.icon, def.name)}
                      </div>
                      <div style={{flex: 1}}>
                        <div style={{color: RARITY_COLORS[def.rarity], fontWeight: 600, fontSize: '13px'}}>
                          {def.name}
                        </div>
                        <div style={{fontSize: '9px', color: '#fff', marginTop: '4px', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 'bold', fontFamily: '"Sometype Mono", monospace', display: 'inline-block', background: '#1f6feb', padding: '2px 6px', borderRadius: 0}}>
                          {def.type}
                        </div>
                      </div>
                      <div style={{textAlign: 'right', marginLeft: '12px', flexShrink: 0}}>
                        <div style={{color: '#22c55e', fontWeight: 600, fontSize: '13px'}}>
                          {sellPrice.toLocaleString()}₱
                        </div>
                        <div style={{color: '#8b949e', fontSize: '11px', marginTop: '2px'}}>
                          Qty: {item.quantity}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Right Panel - Item Details */}
          <div style={styles.detailPanel}>
            {currentItem ? (
              <>
                <div style={styles.detailHeader}>
                  <div style={styles.detailIconBox}>
                    {renderItemIcon(currentItem.icon, currentItem.name)}
                  </div>
                  <div>
                    <h3
                      style={{
                        ...styles.detailName,
                        color: RARITY_COLORS[currentItem.rarity],
                      }}
                    >
                      {currentItem.name}
                    </h3>
                    <span style={styles.detailRarity}>
                      {currentItem.rarity.toUpperCase()} {currentItem.type.toUpperCase()}
                    </span>
                  </div>
                </div>

                <p style={styles.detailDesc}>{currentItem.description}</p>

                {currentItem.stats && Object.keys(currentItem.stats).length > 0 && (
                  <div style={styles.statsBox}>
                    <span style={styles.statsLabel}>STATS</span>
                    <div style={styles.statsList}>
                      {Object.entries(currentItem.stats).map(([key, value]) => (
                        <div key={key} style={styles.statRow}>
                          <span style={styles.statKey}>{key}</span>
                          <span style={styles.statValue}>+{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div style={styles.priceBox}>
                  <div style={styles.priceRow}>
                    <span style={styles.priceLabel}>
                      {activeTab === 'buy' ? 'BUY PRICE' : 'SELL PRICE'}
                    </span>
                    <span
                      style={{
                        ...styles.priceValue,
                        color: activeTab === 'buy' ? '#fbbf24' : '#22c55e',
                      }}
                    >
                      ₱
                      {activeTab === 'buy'
                        ? (selectedItem?.currentBuyPrice || 0).toLocaleString()
                        : (
                            shopItems.find((s) => s.itemId === selectedSellItem?.itemId)
                              ?.currentSellPrice ||
                            selectedSellItem?.definition?.sellPrice ||
                            0
                          ).toLocaleString()}
                    </span>
                  </div>
                  {activeTab === 'buy' && selectedItem && (
                    <div style={styles.priceRow}>
                      <span style={styles.priceLabel}>IN STOCK</span>
                      <span style={styles.priceValue}>
                        {selectedItem.stock}/{selectedItem.maxStock}
                      </span>
                    </div>
                  )}
                  {activeTab === 'sell' && selectedSellItem && (
                    <div style={styles.priceRow}>
                      <span style={styles.priceLabel}>YOU OWN</span>
                      <span style={styles.priceValue}>{selectedSellItem.quantity}</span>
                    </div>
                  )}
                </div>

                {/* Quantity Selector */}
                <div style={styles.quantityBox}>
                  <span style={styles.quantityLabel}>QUANTITY</span>
                  <div style={styles.quantityControls}>
                    <button
                      style={styles.quantityBtn}
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      disabled={quantity <= 1}
                    >
                      -
                    </button>
                    <input
                      type="number"
                      value={quantity}
                      onChange={(e) => {
                        const val = parseInt(e.target.value, 10);
                        if (!isNaN(val)) {
                          setQuantity(Math.max(1, Math.min(maxQuantity, val)));
                        }
                      }}
                      style={styles.quantityInput}
                      min={1}
                      max={maxQuantity}
                    />
                    <button
                      style={styles.quantityBtn}
                      onClick={() => setQuantity(Math.min(maxQuantity, quantity + 1))}
                      disabled={quantity >= maxQuantity}
                    >
                      +
                    </button>
                    <button
                      style={styles.maxBtn}
                      onClick={() => setQuantity(maxQuantity)}
                    >
                      MAX
                    </button>
                  </div>
                </div>

                {/* Total */}
                <div style={styles.totalBox}>
                  <span style={styles.totalLabel}>TOTAL</span>
                  <span style={styles.totalValue}>
                    ₱
                    {(
                      quantity *
                      (activeTab === 'buy'
                        ? selectedItem?.currentBuyPrice || 0
                        : shopItems.find((s) => s.itemId === selectedSellItem?.itemId)
                            ?.currentSellPrice ||
                          selectedSellItem?.definition?.sellPrice ||
                          0)
                    ).toLocaleString()}
                  </span>
                </div>

                {/* Action Button */}
                {activeTab === 'buy' ? (
                  <button
                    style={{
                      ...styles.actionBtn,
                      background:
                        pieces >= (selectedItem?.currentBuyPrice || 0) * quantity &&
                        (selectedItem?.stock || 0) >= quantity
                          ? '#238636'
                          : '#21262d',
                      cursor:
                        pieces >= (selectedItem?.currentBuyPrice || 0) * quantity &&
                        (selectedItem?.stock || 0) >= quantity
                          ? 'pointer'
                          : 'not-allowed',
                    }}
                    onClick={handleBuy}
                    disabled={
                      pieces < (selectedItem?.currentBuyPrice || 0) * quantity ||
                      (selectedItem?.stock || 0) < quantity
                    }
                  >
                    {(selectedItem?.stock || 0) < quantity
                      ? 'NOT ENOUGH STOCK'
                      : pieces < (selectedItem?.currentBuyPrice || 0) * quantity
                      ? 'INSUFFICIENT FUNDS'
                      : `BUY FOR ₱${(
                          (selectedItem?.currentBuyPrice || 0) * quantity
                        ).toLocaleString()}`}
                  </button>
                ) : (
                  <button
                    style={{
                      ...styles.actionBtn,
                      background: '#1f6feb',
                    }}
                    onClick={handleSell}
                  >
                    SELL FOR ₱
                    {(
                      quantity *
                      (shopItems.find((s) => s.itemId === selectedSellItem?.itemId)
                        ?.currentSellPrice ||
                        selectedSellItem?.definition?.sellPrice ||
                        0)
                    ).toLocaleString()}
                  </button>
                )}

                {/* Alternate Prices */}
                {alternatePrices.length > 0 && (
                  <div style={styles.altPricesBox}>
                    <span style={styles.altPricesLabel}>
                      {activeTab === 'buy' ? 'PRICES AT OTHER MARKETS' : 'SELL PRICES ELSEWHERE'}
                    </span>
                    <div style={styles.altPricesList}>
                      {alternatePrices.map((price) => {
                        const currentPrice =
                          activeTab === 'buy'
                            ? selectedItem?.currentBuyPrice || 0
                            : shopItems.find((s) => s.itemId === selectedSellItem?.itemId)
                                ?.currentSellPrice ||
                              selectedSellItem?.definition?.sellPrice ||
                              0;
                        const comparePrice =
                          activeTab === 'buy' ? price.buyPrice : price.sellPrice;
                        const isBetter =
                          activeTab === 'buy'
                            ? comparePrice < currentPrice
                            : comparePrice > currentPrice;
                        return (
                          <div key={price.locationId} style={styles.altPriceRow}>
                            <span style={styles.altPriceLocation}>{price.locationName}</span>
                            <span
                              style={{
                                ...styles.altPriceValue,
                                color: isBetter ? '#22c55e' : '#ef4444',
                              }}
                            >
                              {comparePrice.toLocaleString()}₱
                            </span>
                            <button
                              style={styles.setDestBtn}
                              onClick={() => {
                                onSetDestination?.(price.x, price.y, price.locationName);
                                onClose();
                              }}
                            >
                              GO
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div style={styles.noSelection}>
                <span style={styles.noSelectionIcon}></span>
                <span style={styles.noSelectionText}>Select an item to view details</span>
              </div>
            )}
          </div>
          </>
          )}
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  panelContent: {
    width: 1100,
    maxWidth: '95vw',
    maxHeight: '90vh',
    background: 'linear-gradient(180deg, #0d1117 0%, #0a0e14 100%)',
    border: '2px solid #21262d',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 0 0 1px #161b22, 0 20px 50px rgba(0, 0, 0, 0.8)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: '1px solid #21262d',
    background: 'rgba(0, 0, 0, 0.3)',
    cursor: 'grab',
    userSelect: 'none',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    pointerEvents: 'none',
  },
  headerIcon: {
    fontSize: 32,
  },
  title: {
    margin: 0,
    fontSize: 18,
    color: '#e2e8f0',
    fontFamily: 'monospace',
  },
  subtitle: {
    fontSize: 11,
    color: '#6b7280',
    fontFamily: 'monospace',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    pointerEvents: 'auto',
  },
  balanceBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    padding: '8px 16px',
    background: 'rgba(251, 191, 36, 0.1)',
    border: '1px solid rgba(251, 191, 36, 0.3)',
  },
  balanceLabel: {
    fontSize: 9,
    color: '#fbbf24',
    fontFamily: 'monospace',
    letterSpacing: 1,
  },
  balanceValue: {
    fontSize: 16,
    color: '#fbbf24',
    fontFamily: 'monospace',
    fontWeight: 600,
  },
  restockBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    padding: '8px 16px',
    background: 'rgba(88, 166, 255, 0.1)',
    border: '1px solid rgba(88, 166, 255, 0.3)',
  },
  restockLabel: {
    fontSize: 9,
    color: '#58a6ff',
    fontFamily: 'monospace',
    letterSpacing: 1,
  },
  restockValue: {
    fontSize: 16,
    color: '#58a6ff',
    fontFamily: 'monospace',
    fontWeight: 600,
  },
  closeBtn: {
    width: 36,
    height: 36,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'transparent',
    color: '#8b949e',
    border: '1px solid #30363d',
    cursor: 'pointer',
    fontSize: 14,
  },
  message: {
    margin: '0 20px',
    marginTop: 12,
    padding: '10px 14px',
    fontSize: 12,
    fontFamily: 'monospace',
    border: '1px solid',
  },
  tabs: {
    display: 'flex',
    gap: 0,
    padding: '0',
    borderBottom: '2px solid #21262d',
  },
  tab: {
    padding: '12px 32px',
    background: 'transparent',
    border: 'none',
    borderBottom: '2px solid transparent',
    color: '#8b949e',
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 600,
    fontFamily: 'monospace',
    letterSpacing: 1,
  },
  tabActive: {
    color: '#58a6ff',
    borderBottomColor: '#58a6ff',
  },
  tabRow: {
    display: 'flex',
    gap: 0,
    padding: '12px 20px',
    borderBottom: '1px solid #21262d',
    background: 'rgba(0, 0, 0, 0.2)',
    alignItems: 'center',
  },
  content: {
    display: 'flex',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    height: 420,
  },
  sidebar: {
    width: 180,
    borderRight: '1px solid #21262d',
    display: 'flex',
    flexDirection: 'column',
    background: 'rgba(0, 0, 0, 0.2)',
  },
  searchBox: {
    padding: 12,
    borderBottom: '1px solid #21262d',
  },
  searchInput: {
    width: '100%',
    padding: '8px 10px',
    background: '#0d1117',
    border: '1px solid #30363d',
    color: '#e2e8f0',
    fontSize: 12,
    fontFamily: 'monospace',
    outline: 'none',
  },
  categoryList: {
    flex: 1,
    overflowY: 'auto',
    padding: '8px 0',
  },
  categoryBtn: {
    width: '100%',
    padding: '10px 12px',
    background: 'transparent',
    border: 'none',
    borderLeft: '3px solid transparent',
    color: '#8b949e',
    cursor: 'pointer',
    fontSize: 12,
    fontFamily: 'monospace',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    textAlign: 'left',
  },
  categoryBtnActive: {
    background: 'rgba(88, 166, 255, 0.1)',
    borderLeftColor: '#58a6ff',
    color: '#58a6ff',
  },
  categoryCount: {
    marginLeft: 'auto',
    fontSize: 10,
    opacity: 0.6,
  },
  itemGrid: {
    flex: 1,
    overflowY: 'auto',
    padding: 16,
    // Make scrollbar thinner to reduce visual clutter
    scrollbarWidth: 'thin', // Firefox
  },
  loadingGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: 12,
  },
  skeletonCard: {
    height: 160,
    background: 'linear-gradient(90deg, #1f2937 25%, #374151 50%, #1f2937 75%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.5s ease-in-out infinite',
  },
  gridContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: 12,
  },
  itemCard: {
    padding: 12,
    border: '1px solid #21262d',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    transition: 'all 0.15s ease',
  },
  cardIconBox: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: 60,
  },
  cardInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  cardName: {
    fontSize: 12,
    fontWeight: 600,
    fontFamily: 'monospace',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  cardType: {
    fontSize: 10,
    color: '#6b7280',
    fontFamily: 'monospace',
    textTransform: 'capitalize',
  },
  cardFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 'auto',
    paddingTop: 8,
    borderTop: '1px solid rgba(255,255,255,0.05)',
  },
  cardPrice: {
    fontSize: 12,
    fontWeight: 600,
    fontFamily: 'monospace',
    color: '#fbbf24',
  },
  cardStock: {
    fontSize: 10,
    fontFamily: 'monospace',
  },
  emptyState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 200,
    color: '#6b7280',
    fontSize: 14,
    fontFamily: 'monospace',
  },
  detailPanel: {
    width: 280,
    borderLeft: '1px solid #21262d',
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    overflowY: 'auto',
    background: 'rgba(0, 0, 0, 0.2)',
  },
  detailHeader: {
    display: 'flex',
    gap: 12,
    alignItems: 'flex-start',
  },
  detailIconBox: {
    width: 64,
    height: 64,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(0, 0, 0, 0.3)',
    border: '1px solid #21262d',
  },
  detailName: {
    margin: 0,
    fontSize: 16,
    fontFamily: 'monospace',
  },
  detailDesc: {
    margin: 0,
    fontSize: 12,
    color: '#8b949e',
    lineHeight: 1.5,
    fontFamily: 'monospace',
  },
  statsBox: {
    background: 'rgba(88, 166, 255, 0.05)',
    border: '1px solid rgba(88, 166, 255, 0.2)',
    padding: 12,
  },
  statsLabel: {
    fontSize: 9,
    color: '#58a6ff',
    fontFamily: 'monospace',
    letterSpacing: 1,
    marginBottom: 8,
    display: 'block',
  },
  statsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  statRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: 12,
    fontFamily: 'monospace',
  },
  statKey: {
    color: '#8b949e',
    textTransform: 'capitalize',
  },
  statValue: {
    color: '#22c55e',
  },
  priceBox: {
    background: 'rgba(0, 0, 0, 0.3)',
    padding: 12,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  priceRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  priceLabel: {
    fontSize: 10,
    color: '#6b7280',
    fontFamily: 'monospace',
    letterSpacing: 1,
  },
  priceValue: {
    fontSize: 14,
    fontFamily: 'monospace',
    fontWeight: 600,
  },
  quantityBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  quantityLabel: {
    fontSize: 10,
    color: '#6b7280',
    fontFamily: 'monospace',
    letterSpacing: 1,
  },
  quantityControls: {
    display: 'flex',
    gap: 4,
  },
  quantityBtn: {
    width: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#21262d',
    border: '1px solid #30363d',
    color: '#e2e8f0',
    cursor: 'pointer',
    fontSize: 16,
    fontFamily: 'monospace',
  },
  quantityInput: {
    flex: 1,
    height: 32,
    background: '#0d1117',
    border: '1px solid #30363d',
    color: '#e2e8f0',
    fontSize: 14,
    fontFamily: 'monospace',
    textAlign: 'center',
    outline: 'none',
  },
  maxBtn: {
    padding: '0 12px',
    height: 32,
    background: '#21262d',
    border: '1px solid #30363d',
    color: '#58a6ff',
    cursor: 'pointer',
    fontSize: 10,
    fontFamily: 'monospace',
    fontWeight: 600,
  },
  totalBox: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 12,
    background: 'rgba(251, 191, 36, 0.1)',
    border: '1px solid rgba(251, 191, 36, 0.3)',
  },
  totalLabel: {
    fontSize: 10,
    color: '#fbbf24',
    fontFamily: 'monospace',
    letterSpacing: 1,
  },
  totalValue: {
    fontSize: 18,
    color: '#fbbf24',
    fontFamily: 'monospace',
    fontWeight: 700,
  },
  actionBtn: {
    width: '100%',
    padding: '14px 20px',
    border: 'none',
    color: '#fff',
    fontSize: 12,
    fontFamily: 'monospace',
    fontWeight: 700,
    letterSpacing: 1,
  },
  altPricesBox: {
    marginTop: 'auto',
    paddingTop: 12,
    borderTop: '1px dashed #30363d',
  },
  altPricesLabel: {
    fontSize: 9,
    color: '#6b7280',
    fontFamily: 'monospace',
    letterSpacing: 1,
    marginBottom: 8,
    display: 'block',
  },
  altPricesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  altPriceRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 8px',
    background: 'rgba(0, 0, 0, 0.2)',
    fontSize: 11,
    fontFamily: 'monospace',
  },
  altPriceLocation: {
    flex: 1,
    color: '#8b949e',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  altPriceValue: {
    fontWeight: 600,
  },
  setDestBtn: {
    padding: '4px 8px',
    background: '#1f6feb',
    border: 'none',
    color: '#fff',
    fontSize: 9,
    fontFamily: 'monospace',
    fontWeight: 600,
    cursor: 'pointer',
  },
  noSelection: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    color: '#6b7280',
  },
  noSelectionIcon: {
    fontSize: 48,
    opacity: 0.3,
  },
  noSelectionText: {
    fontSize: 12,
    fontFamily: 'monospace',
  },
};
